import React, { useState, useEffect } from "react";
import LogoUploader from "./LogoUploader";

const CompanyLogos = ({ vendorId, firebase }) => {
  const [logos, setLogos] = useState({
    squareBlack: null,
    squareWhite: null,
    squareColor: null,
    rectangular: null,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [processingLogo, setProcessingLogo] = useState(null);

  useEffect(() => {
    if (vendorId) {
      fetchLogos();
    }
  }, [vendorId]);

  const fetchLogos = async () => {
    setIsLoading(true);
    try {
      const logoDoc = await firebase.db
        .collection("vendors")
        .doc(vendorId)
        .collection("branding")
        .doc("logos")
        .get();

      if (logoDoc.exists) {
        setLogos(logoDoc.data() || {});
      }
    } catch (error) {
      console.error("Error fetching logos:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpload = async (type) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/png, image/jpeg";

    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setProcessingLogo(type);

      // Create a FileReader to check image dimensions
      const reader = new FileReader();
      reader.onload = async (event) => {
        const img = new Image();
        img.onload = async () => {
          let isCorrectSize = false;
          let requiredWidth, requiredHeight;

          // Check dimensions based on type
          if (type === "rectangular") {
            requiredWidth = 500;
            requiredHeight = 250;
            isCorrectSize =
              img.width === requiredWidth && img.height === requiredHeight;
          } else {
            // Square logos
            requiredWidth = requiredHeight = 250;
            isCorrectSize =
              img.width === requiredWidth && img.height === requiredHeight;
          }

          if (!isCorrectSize) {
            alert(
              `Please upload an image that is exactly ${requiredWidth}px × ${requiredHeight}px. Your image is ${img.width}px × ${img.height}px.`
            );
            setProcessingLogo(null);
            return;
          }

          // Continue with upload if dimensions are correct
          try {
            setIsLoading(true);

            // Create a reference in Firebase Storage
            const storageRef = firebase.getStorage().ref();
            const fileRef = storageRef.child(
              `vendors/${vendorId}/logos/${type}_${Date.now()}_${file.name}`
            );

            // Upload the file
            await fileRef.put(file);

            // Get the download URL
            const downloadURL = await fileRef.getDownloadURL();

            // Store the reference in Firestore
            const logoData = {
              url: downloadURL,
              name: file.name,
              storagePath: fileRef.fullPath,
              uploadedAt: new Date(),
              dimensions: `${img.width}×${img.height}`,
            };

            // Update Firestore
            await firebase.db
              .collection("vendors")
              .doc(vendorId)
              .collection("branding")
              .doc("logos")
              .set(
                {
                  [type]: logoData,
                },
                { merge: true }
              );

            // Update local state
            setLogos((prev) => ({
              ...prev,
              [type]: logoData,
            }));
          } catch (error) {
            console.error("Error uploading logo:", error);
            alert("There was an error uploading your logo. Please try again.");
          } finally {
            setIsLoading(false);
            setProcessingLogo(null);
          }
        };
        img.src = event.target.result;
      };
      reader.readAsDataURL(file);
    };

    input.click();
  };

  const handleDelete = async (type) => {
    if (!logos[type]) return;

    try {
      setIsLoading(true);

      // Delete from Storage
      const storageRef = firebase.getStorage().ref();
      const fileRef = storageRef.child(logos[type].storagePath);
      await fileRef.delete();

      // Delete from Firestore
      await firebase.db
        .collection("vendors")
        .doc(vendorId)
        .collection("branding")
        .doc("logos")
        .update({
          [type]: null,
        });

      // Update local state
      setLogos((prev) => ({
        ...prev,
        [type]: null,
      }));
    } catch (error) {
      console.error("Error deleting logo:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="contract_cont">
      <div className="ctr security">
        <div className="ctr_el_header resource-header">
          <div className="header-intro">
            <h3>Company Logos</h3>
          </div>

          <div className="content">
            <p className="lighter" style={{ marginTop: "-8.5px" }}>
              Upload your company logos for use across the Spryte platform.
              These logos will be used in your profile and company materials.
            </p>
          </div>
        </div>
      </div>

      <div className="logo-grid">
        <LogoUploader
          type="squareBlack"
          title="Square Black Logo"
          description="Transparent background, black logo"
          size="250px × 250px"
          logos={logos}
          handleUpload={handleUpload}
          handleDelete={handleDelete}
          isProcessing={processingLogo === "squareBlack"}
        />

        <LogoUploader
          type="squareWhite"
          title="Square White Logo"
          description="Transparent background, white logo"
          size="250px × 250px"
          logos={logos}
          handleUpload={handleUpload}
          handleDelete={handleDelete}
          isProcessing={processingLogo === "squareWhite"}
        />

        <LogoUploader
          type="squareColor"
          title="Square Color Logo"
          description="Transparent background, colored logo"
          size="250px × 250px"
          logos={logos}
          handleUpload={handleUpload}
          handleDelete={handleDelete}
          isProcessing={processingLogo === "squareColor"}
        />

        <LogoUploader
          type="rectangular"
          title="Rectangular Company Logo"
          description="Your official company logo"
          size="500px × 250px"
          logos={logos}
          handleUpload={handleUpload}
          handleDelete={handleDelete}
          isProcessing={processingLogo === "rectangular"}
        />
      </div>

      <div className="contract_bottom">{isLoading && <p>Loading...</p>}</div>
    </div>
  );
};

export default CompanyLogos;
